import React, { useState, useEffect } from "react";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { StatusCode } from "api/protocols";
import { GetPaymentOptions } from "api/rpc/2024-04/facilityAdmin/facility/paymentOption";

import { showError } from "redux/actions/ui";
import { IPaymentOption } from "redux/reducers/models/transaction";
import { valueToString } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import { Badge } from "components/badge/Badge";
import Page from "components/page/Page";
import DataTable from "../../../../../components/dataTable/DataTable";

import "./facility.scss";
import { formatAccountingReferenceLabel } from "utils/formatAccountingReferenceLabel";

export default function FacilityPaymentOptions() {
  const history = useHistory();
  const { t } = useTranslation();
  const authStore = useAppSelector(store => store.authStore);
  const dispatch = useAppDispatch();
  const [paymentOptions, setPaymentOptions] = useState<IPaymentOption[]>(undefined);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadPaymentOptions(source.token);
    return () => source.cancel();
  }, []);

  async function loadPaymentOptions(token?: CancelToken) {
    if (paymentOptions !== undefined) {
      setPaymentOptions(undefined);
    }

    const res = await GetPaymentOptions(null, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading payment options.")));
    }

    setPaymentOptions(res.status !== StatusCode.OK ? [] : res.data as IPaymentOption[]);
  }

  function sortByActive(prev: IPaymentOption, next: IPaymentOption) {
    if (prev.active === next.active) {
      return 0;
    } else {
      if (prev.active) {
        return -1;
      } else {
        return 1;
      }
    }
  }

  const primaryAction = {
    content: t("New Payment Method"),
    action: () => history.push("/admin/settings/facility-payment-options/new"),
  };

  return (
    <Page
      title={t("Payment Methods")}
      primaryAction={authStore?.user?.permissions?.payment_options_create ? primaryAction : null}
      narrow
    >
      <DataTable
        columns={[
          { label: t("Title") },
          { label: t("Processing Type") },
          { label: "" },
          { label: t("Type") },
          { label: t("Accounting Reference") },
        ]}
        loading={paymentOptions === undefined}
      >
        {paymentOptions?.sort(sortByActive).map((paymentOption, index: number) => {
          return (
            <tr
              key={index}
              className="clickable"
              onClick={() => history.push("/admin/settings/facility-payment-options/" + String(paymentOption.id))}
            >
              <td>{paymentOption.title}</td>
              <td>{valueToString(paymentOption.processing_type, "capitalize")}</td>
              <td>
                <Badge type={paymentOption.active ? "success" : "warning"}>
                  {paymentOption.active ? "Active" : "Inactive"}
                </Badge>
              </td>
              <td>{valueToString(paymentOption.type, "capitalize")}</td>
              <td>
                {paymentOption?.accounting_reference
                  ? formatAccountingReferenceLabel(
                      paymentOption.accounting_reference.title ?? "",
                      paymentOption.accounting_reference.reference_number,
                    )
                  : ""}
              </td>
            </tr>
          );
        })}
      </DataTable>
    </Page>
  );
}
